import type { SxProps, Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { SECTION_PAPER_SX } from './section-paper';

/** @deprecated Use SECTION_PAPER_SX or SectionPaper component. */
export const DASHBOARD_SECTION_PAPER_SX: SxProps<Theme> = SECTION_PAPER_SX;

export const DASHBOARD_METRIC_GRID_SX: SxProps<Theme> = {
  display: 'grid',
  gap: 2.5,
  alignItems: 'stretch',
};

export const dashboardMetricGridColumns = (
  columns: { xs?: number; sm?: number; md?: number; lg?: number } = {},
): SxProps<Theme> => ({
  ...DASHBOARD_METRIC_GRID_SX,
  gridTemplateColumns: {
    xs: `repeat(${columns.xs ?? 1}, minmax(0, 1fr))`,
    sm: columns.sm ? `repeat(${columns.sm}, minmax(0, 1fr))` : undefined,
    md: columns.md ? `repeat(${columns.md}, minmax(0, 1fr))` : undefined,
    lg: columns.lg ? `repeat(${columns.lg}, minmax(0, 1fr))` : undefined,
  },
});

export const DASHBOARD_KPI_GRID_SX: SxProps<Theme> = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: {
    xs: 'minmax(0, 1fr)',
    sm: 'repeat(2, minmax(0, 1fr))',
    md: 'repeat(3, minmax(0, 1fr))',
    lg: 'repeat(4, minmax(0, 1fr))',
  },
};

export const dashboardCardSurfaceSx = (theme: Theme): SxProps<Theme> => ({
  borderRadius: '12px',
  bgcolor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  transition: 'border-color 0.2s ease, background-color 0.2s ease',
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.22),
  },
});

export const dashboardNestedCardSx = (theme: Theme): SxProps<Theme> => ({
  p: 2.5,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '12px',
  bgcolor: alpha(theme.palette.text.primary, 0.02),
  border: `1px solid ${theme.palette.divider}`,
  transition: 'border-color 0.2s ease, background-color 0.2s ease',
  '&:hover': {
    bgcolor: alpha(theme.palette.primary.main, 0.03),
    borderColor: alpha(theme.palette.primary.main, 0.18),
  },
});

export const dashboardInnerMetricTileSx = (theme: Theme): SxProps<Theme> => ({
  p: 1.5,
  borderRadius: '10px',
  border: `1px solid ${theme.palette.divider}`,
  bgcolor: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 88,
});

export const dashboardBranchCardSx = (theme: Theme): SxProps<Theme> => ({
  p: { xs: 2, sm: 2.5 },
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  borderRadius: '14px',
  bgcolor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.22),
    boxShadow: `0 4px 20px ${alpha(theme.palette.text.primary, 0.06)}`,
  },
});

export const dashboardBranchKpiGridSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 1.5,
};

/** @deprecated Alias for dashboardBranchKpiGridSx */
export const dashboardBranchImpactGridSx: SxProps<Theme> = dashboardBranchKpiGridSx;

export const dashboardBranchKpiTileSx = (theme: Theme): SxProps<Theme> => ({
  p: 1.5,
  borderRadius: '12px',
  border: `1px solid ${theme.palette.divider}`,
  bgcolor: alpha(theme.palette.text.primary, 0.02),
  display: 'flex',
  flexDirection: 'column',
  gap: 0.75,
  minHeight: 0,
});

export const dashboardBranchUtilityGridSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'minmax(0, 1fr)',
    sm: 'repeat(2, minmax(0, 1fr))',
  },
  gap: 1,
};

export const dashboardBranchUtilityTileSx = (theme: Theme): SxProps<Theme> => ({
  px: 1.5,
  py: 1.25,
  borderRadius: '10px',
  border: `1px solid ${theme.palette.divider}`,
  bgcolor: theme.palette.background.paper,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1.5,
  minWidth: 0,
});

export const dashboardCarbonToolbarSx: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  gap: 2,
  mt: 0.5,
  p: { xs: 1.75, sm: 2 },
  borderRadius: '12px',
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.elevated',
};

export const dashboardCarbonToolbarFieldsSx: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-end',
  gap: 1.5,
  flex: 1,
  minWidth: 0,
};

export const dashboardCarbonFilterFieldSx = (theme: Theme): SxProps<Theme> => ({
  minWidth: { xs: '100%', sm: 168 },
  flex: { xs: '1 1 100%', sm: '0 1 auto' },
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: theme.palette.background.paper,
  },
});

export const dashboardCarbonKpiGridSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'minmax(0, 1fr)' },
  gridTemplateRows: { xs: 'auto', md: 'repeat(4, 1fr)' },
  gap: 1.5,
  width: '100%',
  height: '100%',
};

export const dashboardCarbonKpiTileSx = (theme: Theme): SxProps<Theme> => ({
  ...dashboardBranchKpiTileSx(theme),
  minHeight: { xs: 88, md: 0 },
});

export const dashboardCarbonHeroTileSx = (theme: Theme): SxProps<Theme> => ({
  ...dashboardCarbonKpiTileSx(theme),
  gridColumn: { xs: '1 / -1', md: 'auto' },
  borderColor: alpha(theme.palette.primary.main, 0.28),
  bgcolor: alpha(theme.palette.primary.main, 0.04),
  minHeight: { xs: 100, md: 0 },
});
